<template>
  <div class="estimation">
    <div class="estimation__card">
      <div class="estimation__header">
        <div class="estimation__header--title">{{ $t('estimation.estimation-generation.title') }}</div>
        <div class="estimation__header--subtitle">{{ $t('estimation.estimation-generation.subtitle') }}</div>
      </div>
      <div class="estimation__content">
        <!-- SOURCE -->
        <div class="estimation__section" v-if="hasDocuments">
          <div class="estimation__section--title">
            <span>{{ $t('estimation.estimation-generation.source') }}</span>
          </div>
          <BaseSelect
            name="Queries"
            documentIcon="file-pdf-box"
            :items="opportunity.documents"
            document
            item-text="name"
            item-value="id"
            @change="handleFileSelection"
            :placeholder="$t('estimation.estimation-generation.source-selection')"
          />
          <div class="estimation__loading" v-if="inputFile && isXlsFile(this.inputFile) && sheetNamesPending">
            <span class="estimation__header--subtitle">
              {{ $t('estimation.estimation-generation.excel-sheets-extraction') }}
            </span>
            <BaseCircularLoadingBar :size="16" :width="2" />
          </div>
          <div class="estimation__pages" v-if="showPdfInputs">
            <h3 class="title">{{ $t('estimation.estimation-generation.pages') }}</h3>
            <div class="estimation__inputs">
              <div>
                <div class="estimation__page-container">
                  <!-- TODO: pass maximum pdf page number  -->
                  <PageRangeInput name="pdfPages" @update:selectedPages="handlePdfPages" :max-page="10000" />
                </div>
              </div>
            </div>
          </div>
          <div class="estimation__excel-sheets" v-if="showExcelInputs">
            <BaseSelect
              :label="$t('estimation.estimation-generation.sheet-names')"
              name="subscription"
              :value="sheetName"
              :items="sheetNames"
              item-text="text"
              item-value="value"
              isMultiple
              explicitValues
              :hint="$t('estimation.estimation-generation.sheet-names-label')"
              @change="onSheetChange"
            />
          </div>
        </div>
        <!-- TYPE OF SERVICE -->
        <div class="estimation__section">
          <div class="estimation__section--title">
            <span>{{ $t('estimation.estimation-generation.product-bundle-title') }}</span>
          </div>
          <BaseCircularLoadingBar :size="16" :width="2" v-if="productBundleTemplatesPending" />
          <div v-else class="estimation__choices">
            <div v-for="(template, index) in productBundleTemplates" :key="index">
              <SquareButton
                :buttonText="template.name"
                :buttonDescription="template.description"
                :is-selected="selectedProductBundleTemplate && selectedProductBundleTemplate.id === template.id"
                @click="selectProductBundleTemplate(template)"
              />
            </div>
          </div>
        </div>
        <!-- OUTPUT EXCEL MODEL -->
        <div class="estimation__section">
          <div class="estimation__section--title">
            <span> {{ $t('estimation.estimation-generation.export-model-title') }}</span>
          </div>
          <BaseCircularLoadingBar :size="16" :width="2" v-if="outputFileTemplatesPending" />
          <div v-else class="estimation__choices">
            <div v-if="extractionOutputFileTemplates.length === 0">
              <span> {{ $t('estimation.estimation-generation.no-output-file-template') }}</span>
            </div>
            <div v-else v-for="(template, index) in extractionOutputFileTemplates" :key="index">
              <SquareButton
                :buttonText="template.name"
                actionable
                :is-selected="
                  selectedExtractionOutputFileTemplate && selectedExtractionOutputFileTemplate.id === template.id
                "
                @click="selectExportModel(template)"
                @delete="onDeleteOutputFileTemplate(template)"
              />
            </div>
          </div>
          <BaseButton @click.native="onAddOutputFileTemplate" isTransparent isUnderline isSmall isSquare hasIconLeft>
            <BaseIcon name="plus" />
            {{ $t('actions.add') }}
          </BaseButton>
          <input
            type="file"
            ref="filePicker"
            @change="handleFileUpload"
            size="1"
            class="file-upload__input"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          />
        </div>
        <div class="estimation__actions">
          <BaseButton v-if="isCreateButton" @click.native="createExtraction" :disabled="disableExtractionButton">
            {{ $t('estimation.estimation-generation.button-label') }}
          </BaseButton>
          <BaseButton v-else @click.native="resetExtraction" :disabled="disableExtractionButton">
            {{ $t('estimation.estimation-generation.button-label-reset') }}
          </BaseButton>
        </div>
      </div>
    </div>
    <ConfirmationDialog ref="confirmationDialog" :buttonText="$t('actions.delete')" @execute="deleteOutputFileTemplate">
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="estimation.estimation-generation.delete-output-file-template">
            <template v-slot:templateName v-if="manipulatedObject">
              <span class="confirmation-dialog__document"> {{ manipulatedObject.name }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar';
import BaseSelect from '@/components/base/BaseSelect';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import PageRangeInput from '@/components/form/PageRangeInput.vue';
import SquareButton from '@/components/page-opportunity-estimation/SquareButton.vue';

import { getDocumentExcelSheetsFromUrl, isPdfFile, isXlsFile } from '@/util/document-file-util';
import { mapActions, mapGetters } from 'vuex';
import { BASE_URL } from '@/services/opportunity-documents.service';
import { ApiService } from '@/services/api.service';

export default {
  name: 'EstimationGeneration',
  components: {
    BaseButton,
    BaseIcon,
    BaseCircularLoadingBar,
    BaseSelect,
    ConfirmationDialog,
    PageRangeInput,
    SquareButton,
  },
  props: {
    opportunityId: String,
  },
  data() {
    return {
      manipulatedObject: null,
      showPdfInputs: false,
      showExcelInputs: false,
      sheetNamesPending: false,
      sheetNames: [],
      sheetName: '',
      // Required field for generation
      pdfPages: [],
      xlsSheets: [],
      inputFile: null,
      selectedProductBundleTemplate: null,
      selectedExtractionOutputFileTemplate: null,
    };
  },
  computed: {
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('productBundleTemplates', ['productBundleTemplates']),
    ...mapGetters('productBundleTemplates', { productBundleTemplatesPending: 'pending' }),
    ...mapGetters('extractionOutputFileTemplates', ['extractionOutputFileTemplates']),
    ...mapGetters('productBundleExtractions', ['initiateResetProductBundleExtraction']),
    ...mapGetters('productBundleExtraction', ['productBundleExtraction']),
    ...mapGetters('extractionOutputFileTemplates', { outputFileTemplatesPending: 'pending' }),
    ...mapGetters('pdfData', ['pdf']),
    hasDocuments() {
      return this.opportunity && this.opportunity.documents && this.opportunity.documents.length > 0;
    },
    disableExtractionButton() {
      return (
        !(this.showPdfInputs ? this.pdfPages.length : this.xlsSheets.length) ||
        !this.selectedProductBundleTemplate ||
        !this.selectedExtractionOutputFileTemplate
      );
    },
    isCreateButton() {
      return !this.initiateResetProductBundleExtraction;
    },
  },
  methods: {
    ...mapActions('productBundleTemplates', ['getProductBundleTemplates']),
    ...mapActions('productBundleExtractions', ['addProductBundleExtraction', 'deleteProductBundleExtractionById']),
    ...mapActions('extractionOutputFileTemplates', [
      'getExtractionOutputFileTemplates',
      'addExtractionOutputFileTemplate',
      'deleteExtractionOutputFileTemplate',
    ]),
    isPdfFile,
    isXlsFile,
    onAddOutputFileTemplate() {
      if (this.$refs.filePicker) {
        this.$refs.filePicker.click();
      }
    },
    handleFileUpload() {
      let file = {};
      file = this.$refs.filePicker.files[0];
      this.addOutputFileTemplate(file);
    },
    handlePdfPages(pages) {
      this.pdfPages = pages;
    },
    handleFileSelection(selection) {
      const selectedDocument = this.opportunity.documents.find((doc) => doc.id === selection.documentId);
      if (!selectedDocument) return;
      this.inputFile = selectedDocument;

      this.pdfPages = [];
      this.xlsSheets = [];
      this.showPdfInputs = false;
      this.showExcelInputs = false;
      this.sheetNamesPending = false;

      if (isPdfFile(this.inputFile)) {
        this.showPdfInputs = true;
        // TODO: load/get the pdf page count?
      } else if (isXlsFile(this.inputFile)) {
        this.sheetNamesPending = true;

        // Get the excel sheets
        const pdfUrl = `${BASE_URL}/${this.opportunity.id}/documents/${selectedDocument.id}/file`;

        getDocumentExcelSheetsFromUrl(pdfUrl, ApiService.authorizationHeader()).then((sheetNames) => {
          this.sheetNames = sheetNames;
          this.sheetNamesPending = false;
          this.showExcelInputs = true;
        });
      }
    },
    createExtraction() {
      this.addProductBundleExtraction({
        name: 'Product bundle extraction',
        opportunityDocumentId: this.inputFile.id,
        productBundleTemplateId: this.selectedProductBundleTemplate.id,
        extractionOutputFileTemplateId: this.selectedExtractionOutputFileTemplate.id,
        pdfPages: this.pdfPages,
        xlsSheets: this.xlsSheets,
      });

      // this.$emit('documents');
    },
    async resetExtraction() {
      const { id } = this.productBundleExtraction;

      try {
        await this.deleteProductBundleExtractionById({ id });
        await this.createExtraction();
      } catch (error) {
        this.$toast.error(error);
      }
    },
    onDeleteOutputFileTemplate(template) {
      this.manipulatedObject = template;
      this.$refs.confirmationDialog.openDialog(template);
    },
    deleteOutputFileTemplate() {
      this.deleteExtractionOutputFileTemplate({ templateId: this.manipulatedObject.id })
        .then(() => {
          this.$toast.success(this.$t('estimation.estimation-generation.delete-output-file-template-success'));
        })
        .catch((error) => this.$toast.error(error));
    },
    addOutputFileTemplate(file) {
      let formData = new FormData();
      if (file) formData.append('file[]', file);

      this.addExtractionOutputFileTemplate({ file: formData })
        .then(() => {
          this.$toast.success(this.$t('estimation.estimation-generation.add-output-file-template-success'));
        })
        .catch((error) => this.$toast.error(error));
    },
    selectProductBundleTemplate(template) {
      // If already the one selected, do nothing
      if (this.selectedProductBundleTemplate && template.id === this.selectedProductBundleTemplate.id) return;
      this.selectedProductBundleTemplate = template;
    },
    selectExportModel(template) {
      // If already the one selected, do nothing
      if (this.selectedExtractionOutputFileTemplate && this.selectedExtractionOutputFileTemplate.id === template.id) {
        return;
      }
      this.selectedExtractionOutputFileTemplate = template;
    },
    onSheetChange(value) {
      this.xlsSheets = value;
    },
  },
  async created() {
    await this.getProductBundleTemplates();
    await this.getExtractionOutputFileTemplates();

    // Pre-select first templates
    if (this.productBundleTemplates.length > 0) {
      this.selectedProductBundleTemplate = this.productBundleTemplates[0];
    }
    if (this.extractionOutputFileTemplates.length > 0) {
      this.selectedExtractionOutputFileTemplate = this.extractionOutputFileTemplates[0];
    }
  },
};
</script>

<style lang="scss">
.estimation {
  &__card {
    width: 60%;
    max-width: 750px;
    background-color: $color-white;
    border-radius: $border-radius-root;
    padding: 32px;
    margin: 2% auto 5%;
    position: relative;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &--title {
      font-weight: bold;
      color: $color-normal;
    }
  }

  &__actions {
    padding-top: 32px;
    display: flex;
    justify-content: center;
  }

  &__pages {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 80px;
  }

  &__loading {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    height: 32px;
  }

  &__inputs {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 40px;
  }

  &__page-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;

    .form-input {
      padding: 0 !important;
    }
  }

  &__suggestions {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-bottom: 32px;

    &--title {
      font-size: 32px;
      font-weight: bold;
    }

    &--subtitle {
      font-size: 14px;
      color: $color-normal-pale;
    }
  }

  &__vote-buttons {
    padding: 16px 0;
    display: flex;
    gap: 12px;
  }

  h2 {
    font-size: 14px;
    line-height: 14px;
    font-weight: 700;
    color: $color-normal;
  }

  h3 {
    padding-top: 10px;
    font-size: 14px;
    line-height: 14px;
    font-weight: 400;
    color: $color-normal-pale;
  }

  .badge-container {
    display: inline-flex;
    align-items: center;
    border-radius: $border-radius-large;
    background: $color-accent-100;
    color: $color-white;
    overflow: hidden;
    padding: 2px 12px;
  }

  .badge {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__choices {
    display: flex;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .v-text-field__details {
    display: none !important;
  }

  .base-select {
    height: auto;
  }
}
</style>
